<template>
  <AppHeader>Ticket Detail</AppHeader>

  <Loading v-if="state == 'loading'"></Loading>

  <div v-if="state == 'idle'" class="p-3">
    <div class="p-3 bg-white rounded-lg shadow-md">
      <div class="flex justify-between text-lg font-medium text-gray-500">
        <div>
          <p class="text-base">{{ ticket.category.scope }}</p>
          <p class="leading-none">{{ ticket.category.name }}</p>
        </div>

        <div>
          <p class="text-right">#{{ ticket.id }}</p>
          <p class="text-sm capitalize">{{ ticket.status }}</p>
        </div>
      </div>

      <p class="mt-2 text-gray-700">{{ ticket.description }}</p>

      <p class="mt-2 text-sm leading-none text-gray-500">Created At <b class="text-gray-700">{{ toDate(ticket.created_at, true) }}</b></p>

      <p v-if="ticket.is_owner && ticket.contact" class="mt-2 text-sm leading-none text-gray-500">Created by <b class="text-gray-700">{{ ticket.contact.name }}</b></p>

      <p v-if="ticket.is_contact && ticket.owner" class="mt-2 text-sm leading-none text-gray-500">Handled by <b class="text-gray-700">{{ ticket.owner.name }}</b></p>

      <p v-if="ticket.resolution" class="mt-2 text-gray-700"><span class="text-sm text-gray-500">Resolution:</span> {{ ticket.resolution }}</p>

      <div v-if="ticket.uploads.length > 0" class="mt-2">
        <a v-for="file in ticket.uploads" :key="file.id" :href="file.src" target="_system" class="block mt-2 font-bold leading-none text-gray-500 rounded-md">{{ file.name }}</a>
      </div>
    </div>

    <div class="mt-5" v-if="(ticket.status != 'closed' && ticket.status != 'resolved') && ticket.is_owner">
      <p class="font-bold">Actions</p>
      <div class="flex justify-start my-2 gap-1">
        <button class="px-3 py-1 text-sm leading-none text-black capitalize bg-yellow-400 rounded-full" @click="setForm('escalated')">Escalate</button>
        <button class="px-3 py-1 text-sm leading-none text-black capitalize bg-blue-400 rounded-full" @click="setForm('resolved')">Resolve</button>
        <button class="px-3 py-1 text-sm leading-none text-black capitalize bg-red-400 rounded-full" @click="setForm('closed')">Close</button>
      </div>

      <div class="p-3 mt-2 bg-white rounded-lg shadow-md" v-if="progress.status">
        <div class="px-2 py-1 mb-2 bg-white border-2 rounded-xl border-slate-100">
          <p class="text-gray-600">{{ notesLabels[progress.status] }}</p>
          <textarea v-model="progress.notes" class="w-full text-xl outline-none placeholder:text-lg" placeholder="Enter your Notes"></textarea>
          <p class="w-full mt-1 text-sm text-left text-red-400" v-if="errors.notes">{{ errors.notes }}</p>
        </div>

        <div class="mt-3 text-center">
          <button @click="updateProgress" class="px-3 py-2 text-sm leading-none text-gray-600 uppercase rounded-full bg-slate-300">Update Progress</button>
        </div>
      </div>
    </div>

    <div class="mt-5">
      <p class="font-bold">Messages</p>

      <div v-for="message in filterMessage" :key="message.id" class="p-3 mt-2 bg-white rounded-lg shadow-sm" :class="{'!bg-gray-200': message.type == 'comment'}">
        <p>{{ message.message }}</p>

        <div class="flex items-center justify-between mt-2 text-gray-500">
          <p class="leading-none">{{ message.replier.name }}</p>
          <p class="text-sm leading-none">{{ toDate(message.created_at, true) }}</p>
        </div>

        <div v-if="message.uploads.length > 0" class="mt-2">
          <a v-for="file in message.uploads" :key="file.id" :href="file.src" target="_system" class="block mt-2 font-bold leading-none text-gray-500 rounded-md">{{ file.name }}</a>
        </div>

      </div>
      
      <p v-if="filterMessage.length == 0" class="mt-3 text-xl text-center text-gray-600">No Messages</p>

      <section v-if="(ticket.status != 'closed' && ticket.status != 'resolved')" class="p-2 mt-3 text-gray-600 bg-white shadow-md rounded-xl">
        <div class="px-2 py-1 mb-2 bg-white border-2 rounded-xl border-slate-100">
          <p class="text-gray-600">Message</p>
          <textarea v-model="form.message" class="w-full text-xl focus:outline-none placeholder:text-lg" type="text" placeholder="Enter your Message"></textarea>
          <p class="w-full mt-1 text-sm text-left text-red-400" v-if="errors.message">{{ errors.message }}</p>
        </div>

        <div class="px-2 py-1 mb-2 bg-white border-2 rounded-xl border-slate-100">
          <p class="text-gray-600">Files</p>
          <input class="w-full focus:outline-none file:border-none file:rounded-full file:leading-none file:p-2" type="file" multiple accept=".jpg,.jpeg,.png,.csv,.pdf,.docx,.doc,.xls,.xlsx" @change="uploadFiles">
        </div>

        <div class="flex gap-x-2">
          <button @click="createMessage('reply')" class="w-full px-3 py-2 text-sm leading-none text-gray-600 uppercase rounded-full bg-slate-300">Reply</button>
          <button v-if="ticket.is_owner" @click="createMessage('comment')" class="w-full px-3 py-2 text-sm leading-none text-gray-600 uppercase rounded-full bg-slate-300">Comment</button>
        </div>
      </section>
    </div>
  </div>

</template>

<script>
import AppHeader from '@/components/common/Header.vue';
import Loading from '@/components/common/Loading.vue';
import mixin from '@/utils/mixin';
import formMixin from '@/utils/mixins/form';

export default {
  name: 'TicketsShow',
  mixins: [ mixin, formMixin ],
  components: { AppHeader, Loading },
  data() {
    return {
      ticketId: null,
      ticket: null,
      form: {
        message: null,
        files: null,
      },
      progress: {
        status: '',
        notes: null
      },
      state: 'idle',
      notesLabels: {
        resolved: 'Resolving Notes',
        closed: 'Closing Notes',
        escalated: 'Escalating Notes'
      },
    }
  },

  computed: {
    filterMessage() {
      if (this.ticket.is_contact) {
        return this.ticket.messages.filter(message => message.type === 'reply');
      }

      if (this.ticket.is_owner) {
        return this.ticket.messages;
      }

      return [];
    }
  },

  methods: {
    getData() {
      this.state = 'loading';

      this.request('GET', 'tickets/' + this.ticketId, null, (resp) => {
        this.state = 'idle';
        this.ticket = resp.data.ticket;
      });
    },

    uploadFiles(event) {
      this.form.files = Array.from(event.target.files);
    },

    setForm(type) {
      this.progress.status = type;
    },

    createMessage(type) {
      let rules = {
        message: 'required',
      };

      if (!this.validateForm(this.form, rules)) return;

      const formData = new FormData();
      formData.append('message', this.form.message);
      formData.append('type', type);

      if (this.form.files && this.form.files.length > 0) {
        this.form.files.forEach((f, i) => formData.append(`files[${i}]`, f));
      }

      this.state = 'loading';

      this.request('POST', 'tickets/' + this.ticketId + '/messages', formData, (resp) => {
        this.state = 'idle';

        alert(resp.message ? resp.message : 'Unable to process your request, try again!');

        if (resp.success) {
          this.form = {message: null, files: null};
          this.getData();
        }
      });
    },

    updateProgress() {
      let rules = {
        status: 'required',
        notes: 'required',
      };

      if (!this.validateForm(this.progress, rules)) return;

      this.state = 'loading';

      this.request('POST', 'tickets/' + this.ticketId + '/progress', this.progress, (resp) => {
        this.state = 'idle';

        alert(resp.message ? resp.message : 'Unable to process your request, try again!');

        if (resp.success) {
          this.$router.push({name:'TicketsIndex'});
        }
      });
    },
  },

  created() {
    this.ticketId = this.$route.params.id;
    this.getData();
  },
}
</script>
